import {
  addDays,
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from "date-fns";

export const WEEK_OPTIONS = { weekStartsOn: 1 };

export const PRESETS = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "this_week", label: "This Week" },
  { id: "last_week", label: "Last Week" },
  { id: "this_month", label: "This Month" },
  { id: "last_month", label: "Last Month" },
  { id: "month", label: "Specific month" },
  { id: "day", label: "Specific day" },
  { id: "custom", label: "Custom range" },
];

export function ymd(d) {
  return format(d, "yyyy-MM-dd");
}

export function monthKey(d) {
  return format(d, "yyyy-MM");
}

function asDate(value) {
  if (value instanceof Date) return value;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return parseISO(`${value}T12:00:00`);
  }
  if (typeof value === "string" && /^\d{4}-\d{2}$/.test(value)) {
    return parseISO(`${value}-01T12:00:00`);
  }
  return new Date(value);
}

export function defaultPeriodState() {
  const now = new Date();
  return {
    preset: "this_month",
    month: monthKey(now),
    day: ymd(now),
    from: ymd(startOfMonth(now)),
    to: ymd(now),
  };
}

export function resolvePeriod(state) {
  const now = new Date();
  const preset = state?.preset || "this_month";

  if (preset === "today") {
    return { start: startOfDay(now), end: endOfDay(now), label: "Today", preset };
  }
  if (preset === "yesterday") {
    const d = subDays(now, 1);
    return { start: startOfDay(d), end: endOfDay(d), label: "Yesterday", preset };
  }
  if (preset === "this_week") {
    const start = startOfWeek(now, WEEK_OPTIONS);
    const end = endOfWeek(now, WEEK_OPTIONS);
    return { start, end, label: "This week", preset };
  }
  if (preset === "last_week") {
    const thisStart = startOfWeek(now, WEEK_OPTIONS);
    const start = subDays(thisStart, 7);
    const end = subDays(thisStart, 1);
    return { start: startOfDay(start), end: endOfDay(end), label: "Last week", preset };
  }
  if (preset === "this_month") {
    return {
      start: startOfMonth(now),
      end: endOfMonth(now),
      label: format(now, "MMMM yyyy"),
      preset,
    };
  }
  if (preset === "last_month") {
    const d = subMonths(now, 1);
    return {
      start: startOfMonth(d),
      end: endOfMonth(d),
      label: format(d, "MMMM yyyy"),
      preset,
    };
  }
  if (preset === "month") {
    const d = asDate(state.month || monthKey(now));
    return {
      start: startOfMonth(d),
      end: endOfMonth(d),
      label: format(d, "MMMM yyyy"),
      preset,
    };
  }
  if (preset === "day") {
    const d = asDate(state.day || ymd(now));
    return {
      start: startOfDay(d),
      end: endOfDay(d),
      label: format(d, "MMM d, yyyy"),
      preset,
    };
  }
  const from = asDate(state.from || ymd(startOfMonth(now)));
  const to = asDate(state.to || ymd(now));
  const start = startOfDay(from <= to ? from : to);
  const end = endOfDay(from <= to ? to : from);
  return {
    start,
    end,
    label: `${format(start, "MMM d")} – ${format(end, "MMM d, yyyy")}`,
    preset: "custom",
  };
}

export function callDateKey(call) {
  if (call?.date) return call.date;
  if (call?.startTime) return ymd(new Date(call.startTime));
  return "";
}

export function filterCallsByPeriod(calls, period) {
  const startKey = ymd(period.start);
  const endKey = ymd(period.end);
  return (calls || []).filter((call) => {
    const key = callDateKey(call);
    return key && key >= startKey && key <= endKey;
  });
}

export function daysInPeriod(period) {
  return differenceInCalendarDays(period.end, period.start) + 1;
}

export function elapsedDaysInPeriod(period, now = new Date()) {
  const cap = now < period.end ? now : period.end;
  if (cap < period.start) return 0;
  return differenceInCalendarDays(cap, period.start) + 1;
}

export function eachDayKeys(period) {
  const keys = [];
  const last = ymd(period.end);
  let cur = startOfDay(period.start);
  while (ymd(cur) <= last) {
    keys.push(ymd(cur));
    cur = addDays(cur, 1);
  }
  return keys;
}
